import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { Search, ChevronDown, X, Calendar } from 'lucide-react';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import type { FilterBarProps } from './FilterBar.types';

export const FilterBar: React.FC<FilterBarProps> = ({
  searchPlaceholder = 'Search...',
  searchValue,
  onSearchChange,
  filters = [],
  onFilterChange,
  activeFilters = {},
  onClearFilters,
  showDateRange = false,
  className,
}) => {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const activeCount = Object.values(activeFilters).filter((v) => v && v !== 'all').length;

  return (
    <div
      data-testid="filter-bar"
      className={cn(
        'flex flex-wrap items-center gap-3 p-4 rounded-lg',
        'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800',
        className
      )}
    >
      <div className="relative flex-1 min-w-[200px] max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          data-testid="filter-search-input"
          type="text"
          placeholder={searchPlaceholder}
          value={searchValue || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSearchChange?.(e.target.value)}
          className="pl-9"
        />
      </div>

      {filters.map((filter) => (
        <div key={filter.key} className="relative">
          <button
            data-testid={`filter-${filter.key}-btn`}
            onClick={() => setOpenDropdown(openDropdown === filter.key ? null : filter.key)}
            className={cn(
              'flex items-center gap-2 h-9 px-3 rounded-md text-sm font-medium',
              'border border-slate-200 dark:border-slate-700',
              'hover:bg-slate-50 dark:hover:bg-slate-800',
              'transition-colors duration-150',
              activeFilters[filter.key] && activeFilters[filter.key] !== 'all'
                ? 'bg-indigo-50 dark:bg-indigo-950 border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300'
                : 'text-slate-600 dark:text-slate-400'
            )}
          >
            {filter.icon && <filter.icon className="w-4 h-4" />}
            <span>{filter.label}</span>
            <ChevronDown className={cn(
              'w-4 h-4 transition-transform duration-150',
              openDropdown === filter.key && 'rotate-180'
            )} />
          </button>

          {openDropdown === filter.key && (
            <div
              data-testid={`filter-${filter.key}-dropdown`}
              className={cn(
                'absolute top-full left-0 mt-1 min-w-[160px] py-1 rounded-md z-20',
                'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800',
                'shadow-lg animate-fade-in'
              )}
            >
              {filter.options.map((option) => (
                <button
                  key={option.value}
                  data-testid={`filter-${filter.key}-option-${option.value}`}
                  onClick={() => {
                    onFilterChange?.(filter.key, option.value);
                    setOpenDropdown(null);
                  }}
                  className={cn(
                    'w-full text-left px-3 py-2 text-sm',
                    'hover:bg-slate-50 dark:hover:bg-slate-800',
                    'transition-colors duration-150',
                    activeFilters[filter.key] === option.value
                      ? 'text-indigo-600 dark:text-indigo-400 font-medium'
                      : 'text-slate-700 dark:text-slate-300'
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}

      {showDateRange && (
        <Button
          data-testid="filter-date-range-btn"
          variant="outline"
          className="gap-2"
        >
          <Calendar className="w-4 h-4" />
          <span>Date Range</span>
        </Button>
      )}

      {activeCount > 0 && (
        <Button
          data-testid="filter-clear-btn"
          variant="ghost"
          size="sm"
          onClick={onClearFilters}
          className="text-slate-500 hover:text-slate-700"
        >
          <X className="w-4 h-4" />
          Clear ({activeCount})
        </Button>
      )}
    </div>
  );
};
